import type { LiveActivity, LiveActivityRole, LiveActivityType, LiveQuestion } from '@/api/live'
import { fromQuestionDraft, newKey, questionProblem, toQuestionDraft, type QuestionDraft } from './QuestionEditor'

export const MAX_ACTIVITIES = 12

/** An editable activity: the wire shape (questions as drafts) plus a client-only key for React and drag and drop. */
export type Draft = Omit<LiveActivity, 'questions'> & { key: string; questions: QuestionDraft[] }

/** A saved activity keeps its id as its key, so it stays open (or closed) across a save. */
export const toDraft = (a: LiveActivity): Draft => ({ ...a, key: a.id ?? newKey(), questions: a.questions.map(toQuestionDraft) })

export const fromDraft = ({ key: _key, ...a }: Draft): LiveActivity => ({
  ...a,
  name: a.name.trim(),
  prompt: a.type === 'wordcloud' || a.type === 'qa' || a.type === 'pulse' ? (a.prompt?.trim() || null) : null,
  maxWords: a.type === 'wordcloud' ? (a.maxWords ?? 3) : null,
  questions: a.type === 'quiz' || a.type === 'confidence' || a.type === 'race' ? a.questions.map(fromQuestionDraft) : [],
  timeLimitSeconds: a.type === 'race' ? (a.timeLimitSeconds ?? null) : null,
  oneAtATime: a.type === 'quiz' && !a.role ? !!a.oneAtATime : false,
})

export const TYPE_LABEL: Record<LiveActivityType, string> = {
  quiz: 'Quiz', confidence: 'Confidence check', wordcloud: 'Word cloud', qa: 'Q&A', pulse: 'Understanding check', race: 'Fastest finger',
}

/** One short line saying what each kind of activity is, shown in the open editor. */
export const TYPE_BLURB: Record<LiveActivityType, string> = {
  quiz: 'Questions attendees answer at their own pace. Mark right answers to score it.',
  confidence: 'Ask how confident people feel about each skill, from 1 to 10.',
  wordcloud: 'Attendees send a few words and the room sees them grow by how often they come up.',
  qa: 'Attendees ask questions and upvote each other’s. Run it on its own, as the only open activity.',
  pulse: 'One tap to say with you, not sure or lost. Run it as often as you like: each run is a round.',
  race: 'Single-answer questions played as a race: the quickest right answer wins each one. Just for fun, so nothing is saved to the booking.',
}

export const ROLES: Record<'quiz' | 'confidence', { value: LiveActivityRole; label: string }[]> = {
  quiz: [
    { value: 'pre', label: 'Pre-course quiz' },
    { value: 'post', label: 'Post-course quiz' },
    { value: 'feedback', label: 'Feedback form' },
  ],
  confidence: [{ value: 'before', label: 'Before the course' }, { value: 'after', label: 'After the course' }],
}

export const ROLE_LABEL: Record<LiveActivityRole, string> = {
  pre: 'Pre-course', post: 'Post-course', feedback: 'Feedback', before: 'Before', after: 'After',
}

export const ROLE_HINT: Partial<Record<LiveActivityRole, string>> = {
  pre: 'With a post-course quiz, shows how much people learned.',
  post: 'With a pre-course quiz, shows how much people learned.',
  feedback: 'Its first rating question becomes the headline feedback score.',
  before: 'With an "after" check, shows the change in confidence.',
  after: 'With a "before" check, shows the change in confidence.',
}

/** Colours for the type chip, matching the presenter console. */
export const TYPE_CHIP: Record<LiveActivityType, string> = {
  quiz: 'bg-indigo-50 text-indigo-700',
  confidence: 'bg-teal-50 text-teal-700',
  wordcloud: 'bg-pink-50 text-pink-700',
  qa: 'bg-amber-50 text-amber-700',
  pulse: 'bg-green-50 text-green-700',
  race: 'bg-yellow-50 text-yellow-800',
}

export function blank(
  type: LiveActivityType, name: string, role: LiveActivityRole | null = null, questions: LiveQuestion[] = [],
): Draft {
  return toDraft({
    id: null, type, name, role, questions,
    prompt: type === 'wordcloud' ? 'One word for how you feel about this topic' : null,
    maxWords: type === 'wordcloud' ? 3 : null,
    timeLimitSeconds: null,
    oneAtATime: false,
  })
}

/** A copy of an activity's questions, keeping their ids so a before and an after ask exactly the same thing. */
export const copyQuestions = (from: Draft): QuestionDraft[] =>
  from.questions.map(q => ({ ...toQuestionDraft(fromQuestionDraft(q)), id: q.id }))

/** What is wrong with an activity that stops it being saved, or null. */
export function problemWith(a: Draft, all: Draft[]): string | null {
  if (!a.name.trim()) return 'Give it a name.'
  if (a.type === 'wordcloud' && !a.prompt?.trim()) return 'Add the prompt attendees will see.'
  if (a.type === 'quiz' || a.type === 'confidence' || a.type === 'race') {
    if (a.questions.length === 0) return a.type === 'confidence' ? 'Add at least one statement.' : 'Add at least one question.'
    if (a.questions.some(q => questionProblem(q, a.type === 'race'))) return 'Fix the highlighted question.'
  }
  if (a.role && all.some(o => o !== a && o.type === a.type && o.role === a.role)) return 'Another activity already has this role — each can be used once.'
  return null
}

/** The short line under an activity's name when it is collapsed. */
export function summaryOf(a: Draft): string {
  const n = a.questions.length
  switch (a.type) {
    case 'quiz': {
      const scored = a.questions.some(q => ((q.type === 'choice' || q.type === 'truefalse') && q.correctIndex !== null) || (q.type === 'multiple' && !!q.correctIndexes?.length))
      return n === 0 ? 'No questions yet' : `${n} question${n === 1 ? '' : 's'}${scored ? ' · scored' : ''}${a.oneAtATime && !a.role ? ' · one at a time' : ''}`
    }
    case 'confidence':
      return n === 0 ? 'No statements yet' : `${n} statement${n === 1 ? '' : 's'} rated 1–10`
    case 'wordcloud': {
      const words = a.maxWords ?? 3
      return `Up to ${words} word${words === 1 ? '' : 's'} each${a.prompt?.trim() ? ` · “${a.prompt.trim()}”` : ''}`
    }
    case 'qa':
      return 'Open questions from the room, on its own'
    case 'race':
      return n === 0 ? 'No questions yet' : `${n} question${n === 1 ? '' : 's'} · ${a.timeLimitSeconds ? `${a.timeLimitSeconds}s each` : 'no time limit'}`
    case 'pulse':
      return a.prompt?.trim() ? `“${a.prompt.trim()}”` : 'With you / not sure / lost · run it again for new rounds'
  }
}
